import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, getDay, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAppointmentsStore, Appointment } from '@/store/appointmentsStore';

interface LeadMiniCalendarProps {
  leadId: string;
}

export function LeadMiniCalendar({ leadId }: LeadMiniCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchByLead = useAppointmentsStore(state => state.fetchByLead);

  useEffect(() => {
    if (leadId) {
      setLoading(true);
      fetchByLead(leadId).then(data => {
        setAppointments(data || []);
        setLoading(false);
      });
    }
  }, [leadId, fetchByLead]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOffset = getDay(monthStart);

  const prevMonth = () => setCurrentMonth(prev => subMonths(prev, 1));
  const nextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));

  // Upcoming appointments
  const upcomingAppts = appointments
    .filter(a => a.agendado_para && a.status !== 'cancelado')
    .sort((a, b) => new Date(a.agendado_para).getTime() - new Date(b.agendado_para).getTime())
    .slice(0, 3);

  const capitalizedMonth = format(currentMonth, 'MMMM yyyy', { locale: ptBR });
  const monthLabel = capitalizedMonth.charAt(0).toUpperCase() + capitalizedMonth.slice(1);

  return (
    <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-800">
      <h3 className="text-[10px] font-bold text-[#94A3B8] dark:text-gray-500 uppercase tracking-[0.1em] px-2 flex items-center justify-between">
        <span className="flex items-center gap-1">
          <Calendar size={12} /> AGENDAMENTOS DO LEAD
        </span>
        {loading && <span className="text-[9px] text-blue-500 lowercase animate-pulse">carregando...</span>}
      </h3>

      <div className="bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-3 px-1">
          <button 
            type="button"
            onClick={prevMonth}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 transition-colors cursor-pointer"
            title="Mês anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
            {monthLabel}
          </span>
          <button 
            type="button"
            onClick={nextMonth}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 transition-colors cursor-pointer"
            title="Próximo mês"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Days of Week Header */}
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-gray-400 dark:text-gray-500 mb-1">
          {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
            <div key={i} className="py-1">{d}</div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 text-center text-xs">
          {/* Offset Padding */}
          {Array.from({ length: startDayOffset }).map((_, i) => (
            <div key={`offset-${i}`} className="h-7" />
          ))}

          {/* Days */}
          {days.map((day) => {
            const dayAppts = appointments.filter(a => a.agendado_para && a.status !== 'cancelado' && isSameDay(parseISO(a.agendado_para), day));
            const hasAppt = dayAppts.length > 0;
            const isToday = isSameDay(day, new Date());

            return (
              <div 
                key={day.toISOString()} 
                title={hasAppt ? `${dayAppts.length} agendamento(s)` : undefined}
                className={`h-7 flex flex-col items-center justify-center rounded-lg text-[11px] font-medium transition-all relative ${
                  hasAppt 
                    ? 'bg-blue-600 text-white font-bold shadow-xs' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                } ${isToday && !hasAppt ? 'ring-2 ring-blue-500/50 text-blue-600 dark:text-blue-400 font-bold' : ''}`}
              >
                <span>{format(day, 'd')}</span>
                {hasAppt && (
                  <span className="w-1 h-1 bg-white rounded-full absolute bottom-0.5" />
                )}
              </div>
            );
          })}
        </div>

        {/* Next 3 Appointments */}
        {upcomingAppts.length > 0 ? (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-2">
            <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block px-1">
              Próximos compromissos
            </span>
            {upcomingAppts.map(a => {
              const dateObj = parseISO(a.agendado_para);
              return (
                <div key={a.id} className="text-[11px] p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg flex items-start gap-2 border border-gray-100 dark:border-gray-800/80">
                  <Clock size={13} className="text-blue-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-gray-200 truncate">
                      {a.titulo || a.descricao || 'Agendamento'}
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">
                      {format(dateObj, "dd/MM 'às' HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div className="mt-3 pt-2 text-center text-[10px] text-gray-400 dark:text-gray-500 italic">
              Nenhum agendamento para este lead.
            </div>
          )
        )}
      </div>
    </div>
  );
}
